import { CSSProperties, ReactNode } from 'react';
import { scroller } from 'react-scroll';

import { CheckOutlined } from '@ant-design/icons';

import { BodilColor, NorlysColor, NorlysFont } from '~src/branding';
import { useBranding } from '~src/context/BrandingContext';

import { Heading, headingStyle } from '../Typography';
import { StepIcon } from './style';

export const getStepStatus = (completed: boolean, isCurrentStep: boolean) =>
  isCurrentStep ? 'process' : completed ? 'finish' : 'wait';

export const scrollToStage = (stepKeys: string[], step: number) =>
  setTimeout(() =>
    scroller.scrollTo(`#${stepKeys[step]}`, {
      duration: 500,
      smooth: 'easeInOutQuint',
      containerId: `#${stepKeys[step]}`,
    })
  );

export type StepsIconProps = {
  index: number;
  status: string;
  title?: string;
  chevron?: ReactNode;
};

export const StepsIconNorlys = ({ index, status, title, chevron }: StepsIconProps) => {
  const stage = index + 1;

  const fontStyle = NorlysFont.style('heading', 20);
  const iconFontStyle = NorlysFont.style('default', 14);

  switch (status) {
    case 'finish':
      return (
        <IconWrapperNorlys {...{ title, chevron, fontStyle }}>
          <StepIcon {...{ color: NorlysColor.secondary.mint, fontStyle: iconFontStyle }}>{stage}</StepIcon>
        </IconWrapperNorlys>
      );
    case 'wait':
      return (
        <IconWrapperNorlys {...{ title, chevron, fontStyle }}>
          <StepIcon {...{ textColor: BodilColor.greyscale[20], fontStyle: iconFontStyle }}>{stage}</StepIcon>
        </IconWrapperNorlys>
      );
    case 'process':
    default:
      return (
        <IconWrapperNorlys {...{ title, chevron, fontStyle }}>
          <StepIcon {...{ color: NorlysColor.primary.acid, textColor: 'white', fontStyle: iconFontStyle }}>
            {stage}
          </StepIcon>
        </IconWrapperNorlys>
      );
  }
};

export const StepsIcon = ({ index, status, title, chevron }: StepsIconProps) => {
  const stage = index + 1;

  const bodilGrey = BodilColor.greyscale[20];
  const { darkColor } = useBranding();

  switch (status) {
    case 'finish':
      return (
        <IconWrapper {...{ title, chevron }}>
          <StepIcon {...{ color: darkColor, textColor: 'white' }}>
            <CheckOutlined />
          </StepIcon>
        </IconWrapper>
      );
    case 'wait':
      return (
        <IconWrapper {...{ title, chevron }}>
          <StepIcon {...{ color: 'white', textColor: bodilGrey, borderColor: bodilGrey }}>{stage}</StepIcon>
        </IconWrapper>
      );
    case 'process':
    default:
      return (
        <IconWrapper {...{ title, chevron }}>
          <StepIcon {...{ color: darkColor, textColor: 'white' }}>{stage}</StepIcon>
        </IconWrapper>
      );
  }
};

type IconWrapperProps = {
  title?: string;
  chevron?: ReactNode;
  children?: ReactNode;
  fontStyle?: CSSProperties;
};

const IconWrapper = ({ title, children, chevron }: IconWrapperProps) => (
  <div style={{ display: 'flex', alignItems: 'center' }}>
    {children}
    <Heading level={2} style={{ marginLeft: '8px', fontWeight: 200 }}>
      {title}
    </Heading>
    {chevron && <div style={{ marginLeft: 'auto' }}>{chevron}</div>}
  </div>
);

const IconWrapperNorlys = ({ title, children, chevron, fontStyle = headingStyle(1) }: IconWrapperProps) => (
  <div style={{ display: 'flex', alignItems: 'center', ...fontStyle }}>
    {children}
    <div style={{ marginLeft: '8px' }}>{title}</div>
    {chevron && <div style={{ marginLeft: 'auto' }}>{chevron}</div>}
  </div>
);
